import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import VitrineReactApp from './v2/App.js';

declare global {
  interface Window {
    onAppLogout?: () => void;
  }
}

const Vitrine: React.FC = () => {
  const { logout, user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // Hook deve ser chamado antes de qualquer return condicional
  useEffect(() => {
    // Preserve existing body/html styles to restore later
    let previousBodyClass = document.body.className;
    let prevHtmlOverflow = document.documentElement.style.overflow;
    let prevBodyOverflow = document.body.style.overflow;

    // Só executa a lógica se o usuário estiver autenticado
    if (!loading && isAuthenticated) {
      // Enable scroll on this page (global.css sets overflow hidden)
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';

    // Expose a logout handler for the embedded React header
    window.onAppLogout = async () => {
      try {
        await logout();
        // After logout, redirect to login
        navigate('/login');
      } catch (error) {
        console.error('Erro ao fazer logout:', error);
      }
    };

    // Provide a global cleanup utility that forces removal of vitrine assets (used by logout)
    (window as any).cleanupVitrineAssets = () => {
      try {
        document.querySelectorAll('link[data-vitrine-styles], link#dark-styles, link[href*="/vitrine_assets/"]')
          .forEach(el => el.remove());
        document.querySelectorAll('script[src*="/vitrine_assets/"], script[data-vitrine-script]').forEach(s => s.remove());
      } catch (e) {
        // ignore
      }
    };

    // Ensure styles required by the new vitrines are loaded
    let stylesLink: HTMLLinkElement | null = document.querySelector('link[data-vitrine-styles]');
    if (!stylesLink) {
      stylesLink = document.createElement('link');
      stylesLink.rel = 'stylesheet';
      stylesLink.href = '/vitrine_assets/styles.css';
      stylesLink.setAttribute('data-vitrine-styles', 'true');
      document.head.appendChild(stylesLink);
    }
    // Guarantee ordering: dark stylesheet must come AFTER base to avoid overrides
    const ensureDarkStylesLast = () => {
      const darkEl = document.getElementById('dark-styles') as HTMLLinkElement | null;
      if (darkEl) {
        document.head.appendChild(darkEl); // moves to the end if already in head
      }
    };

    // Sincroniza o tema global com a Vitrine sem forçar dark
    // Alinha a chave 'theme' (usada dentro da Vitrine v2) com 'darkMode' global
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');

    const id = 'dark-styles';
    if (darkMode) {
      document.body.classList.add('dark-mode');
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = '/vitrine_assets/styles-dark.css';
        document.head.appendChild(link);
      }
      // Ensure correct order after potential insertions above
      ensureDarkStylesLast();
    } else {
      document.body.classList.remove('dark-mode');
      const darkLink = document.getElementById(id);
      if (darkLink) darkLink.remove();
    }

    }

    // Cleanup function será sempre chamada, independente da condição
    return () => {
      // Cleanup
      if (window.onAppLogout) {
        delete window.onAppLogout;
      }
      try { delete (window as any).cleanupVitrineAssets; } catch (e) { /* ignore */ }
      // Restore previous styles/state
      document.body.className = previousBodyClass;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [logout, navigate, darkMode, loading, isAuthenticated]);

  // While auth is being validated show nothing (prevents flicker)
  if (loading) return null;

  // If not authenticated, show explicit message and do NOT load vitrines/styles
  if (!isAuthenticated) {
    return (
      <div style={{minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24}}>
        <div style={{maxWidth: 760, width: '100%', textAlign: 'center', background: 'var(--card-bg, #fff)', padding: 24, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.08)'}}>
          <h2 style={{marginBottom: 12}}>Acesso restrito</h2>
          <p style={{marginBottom: 18}}>Você precisa estar autenticado para acessar a vitrine. Por favor faça login para continuar.</p>
          <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
            <button className="btn btn-outline" onClick={() => navigate('/login')}>Ir para o Login</button>
            <button className="btn btn-primary" onClick={() => navigate('/')}>Voltar</button>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated but email not confirmed, show explicit message and block the Vitrine
  if (user && user.emailConfirmed === false) {
    return (
      <div style={{minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px'}}>
        <div style={{maxWidth: 720, textAlign: 'center', background: 'var(--card-bg, #fff)', padding: 24, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.08)'}}>
          <h2 style={{marginBottom: 12}}>E-mail não confirmado</h2>
          <p style={{marginBottom: 18}}>Seu e-mail ainda não foi confirmado. Para acessar a vitrine, por favor verifique a sua caixa de entrada e clique no link de confirmação enviado para <strong>{user.email}</strong>.</p>
          <p style={{marginBottom: 18}}>Se não recebeu o e-mail, verifique a pasta de spam ou entre em contato com o suporte.</p>
          <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
            <button className="btn btn-outline" onClick={() => navigate('/login')}>Voltar ao login</button>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>Recarregar</button>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise load the Vitrine app
  return (
    <div>
      
      <VitrineReactApp />
    </div>
  );
};

export default Vitrine;
